import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { liassesApi } from '../services/api';
import './LiassesFiscales.css';

// Module « Liasses fiscales » (Labo Tools) — extraction structurée par IA + chat.
// Niveau 1 : onglets Dossiers / Configuration. Niveau 2 : espace dossier
// (documents + résultats éditables + export Excel + chat).

const TYPES_DONNEE = [
  { v: 'texte', label: 'Texte' },
  { v: 'montant', label: 'Montant (€)' },
  { v: 'nombre', label: 'Nombre' },
  { v: 'pourcentage', label: 'Pourcentage' },
  { v: 'date', label: 'Date' },
];
const ING = {
  en_attente: { label: 'En attente', cls: 'info', spin: true, icon: '⏳' },
  ocr:        { label: 'OCR / indexation…', cls: 'info', spin: true, icon: '🔍' },
  indexe:     { label: 'Indexé', cls: 'success', spin: false, icon: '✅' },
  erreur:     { label: 'Erreur', cls: 'error', spin: false, icon: '⚠️' },
};
const EXT = {
  en_attente: { label: 'À analyser', cls: 'muted', spin: false, icon: '•' },
  en_cours:   { label: 'Analyse…', cls: 'info', spin: true, icon: '⚙️' },
  extrait:    { label: 'Analysé', cls: 'success', spin: false, icon: '✅' },
  erreur:     { label: 'Erreur', cls: 'error', spin: false, icon: '⚠️' },
};
const isTerminal = (s) => s === 'indexe' || s === 'erreur';
const extTerminal = (s) => s === 'extrait' || s === 'erreur';

function humanSize(b) { if (!b) return ''; const u = ['o', 'Ko', 'Mo', 'Go']; let i = 0, n = b; while (n >= 1024 && i < 3) { n /= 1024; i++; } return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`; }
function relDate(d) { if (!d) return ''; const s = (Date.now() - new Date(d).getTime()) / 1000; if (s < 60) return "à l'instant"; if (s < 3600) return `il y a ${Math.floor(s / 60)} min`; if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`; if (s < 2592000) return `il y a ${Math.floor(s / 86400)} j`; return new Date(d).toLocaleDateString('fr-FR'); }

export default function LiassesFiscales({ user, setHeaderAction, editMode }) {
  const [tab, setTab] = useState('dossiers');
  const [openDossier, setOpenDossier] = useState(null);

  useEffect(() => {
    if (!setHeaderAction) return;
    if (openDossier) setHeaderAction({ secondary: [{ label: '← Tous les dossiers', onClick: () => setOpenDossier(null) }] });
    else setHeaderAction(null);
  }, [openDossier, setHeaderAction]);

  if (openDossier) {
    return <DossierWorkspace dossier={openDossier} user={user} editMode={editMode} onBack={() => setOpenDossier(null)} />;
  }

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={tab === 'dossiers' ? 'active' : ''} onClick={() => setTab('dossiers')}>📂 Dossiers</button>
        <button className={tab === 'config' ? 'active' : ''} onClick={() => setTab('config')}>⚙️ Configuration</button>
      </div>
      {tab === 'dossiers'
        ? <DossiersTab user={user} editMode={editMode} onOpen={setOpenDossier} />
        : <ConfigTab />}
    </div>
  );
}

// ═══════════════════════════ Configuration (types + champs) ═══════════════════
function ConfigTab() {
  const [types, setTypes] = useState([]);
  const [sel, setSel] = useState(null);
  const [fields, setFields] = useState([]);
  const [typeModal, setTypeModal] = useState(null);
  const [fieldModal, setFieldModal] = useState(null);
  const [err, setErr] = useState('');

  const loadTypes = useCallback(async () => {
    const r = await liassesApi.listTypes();
    setTypes(r.data.types);
    setSel((s) => s || r.data.types[0] || null);
  }, []);
  useEffect(() => { loadTypes(); }, [loadTypes]);

  const loadFields = useCallback(async (typeId) => {
    if (!typeId) { setFields([]); return; }
    const r = await liassesApi.listFields(typeId);
    setFields(r.data.fields);
  }, []);
  useEffect(() => { loadFields(sel?.id); }, [sel, loadFields]);

  const saveType = async () => {
    if (!typeModal.nom.trim()) { setErr('Le nom est requis'); return; }
    try {
      if (typeModal.id) await liassesApi.updateType(typeModal.id, { nom: typeModal.nom, description: typeModal.description });
      else { const r = await liassesApi.createType({ nom: typeModal.nom, description: typeModal.description }); setSel(r.data.type); }
      setTypeModal(null); setErr(''); await loadTypes();
    } catch (e) { setErr(e.response?.data?.error || 'Erreur'); }
  };
  const delType = async (t) => { if (!window.confirm(`Supprimer le type « ${t.nom} » et ses champs ?`)) return; await liassesApi.deleteType(t.id); if (sel?.id === t.id) setSel(null); await loadTypes(); };

  const saveField = async () => {
    if (!fieldModal.nom.trim()) { setErr('Le nom est requis'); return; }
    try {
      if (fieldModal.id) await liassesApi.updateField(fieldModal.id, { nom: fieldModal.nom, description: fieldModal.description, type_donnee: fieldModal.type_donnee });
      else await liassesApi.createField(sel.id, { nom: fieldModal.nom, description: fieldModal.description, type_donnee: fieldModal.type_donnee });
      setFieldModal(null); setErr(''); await loadFields(sel.id); await loadTypes();
    } catch (e) { setErr(e.response?.data?.error || 'Erreur'); }
  };
  const delField = async (f) => { if (!window.confirm(`Supprimer le champ « ${f.nom} » ?`)) return; await liassesApi.deleteField(f.id); await loadFields(sel.id); await loadTypes(); };
  const move = async (f, dir) => {
    const idx = fields.findIndex((x) => x.id === f.id);
    const j = idx + dir; if (j < 0 || j >= fields.length) return;
    const other = fields[j];
    await liassesApi.updateField(f.id, { ordre: other.ordre });
    await liassesApi.updateField(other.id, { ordre: f.ordre });
    await loadFields(sel.id);
  };

  return (
    <div className="lf-config">
      <div className="lf-config-types">
        <div className="lf-config-head">
          <h3>Types de documents</h3>
          <button className="btn-primary sm" onClick={() => { setTypeModal({ nom: '', description: '' }); setErr(''); }}>+ Type</button>
        </div>
        {types.length === 0 ? <p className="lf-muted">Aucun type. Créez « Liasse fiscale », « Rapport de gestion »…</p> : (
          <ul className="lf-type-list">
            {types.map((t) => (
              <li key={t.id} className={`lf-type-item ${sel?.id === t.id ? 'active' : ''}`} onClick={() => setSel(t)}>
                <div className="lf-type-main"><span className="lf-type-nom">{t.nom}</span><span className="lf-type-count">{t.nb_champs} champ{t.nb_champs > 1 ? 's' : ''}</span></div>
                <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                  <button className="btn-icon" title="Modifier" onClick={() => { setTypeModal({ ...t }); setErr(''); }}>✏️</button>
                  <button className="btn-icon delete" title="Supprimer" onClick={() => delType(t)}>🗑️</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="lf-config-fields">
        <div className="lf-config-head">
          <h3>Champs à extraire {sel ? `— ${sel.nom}` : ''}</h3>
          {sel && <button className="btn-primary sm" onClick={() => { setFieldModal({ nom: '', description: '', type_donnee: 'texte' }); setErr(''); }}>+ Champ</button>}
        </div>
        {!sel ? <p className="lf-muted">Sélectionnez un type à gauche.</p>
          : fields.length === 0 ? <p className="lf-muted">Aucun champ. Ajoutez « Chiffre d'affaires N », « Résultat net »…</p> : (
            <table className="lf-fields-table">
              <thead><tr><th>Champ</th><th>Consigne IA</th><th>Type</th><th></th></tr></thead>
              <tbody>
                {fields.map((f, i) => (
                  <tr key={f.id}>
                    <td className="lf-field-nom">{f.nom}</td>
                    <td className="lf-field-desc">{f.description || <span className="lf-muted">—</span>}</td>
                    <td>{TYPES_DONNEE.find((t) => t.v === f.type_donnee)?.label || f.type_donnee}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Monter" disabled={i === 0} onClick={() => move(f, -1)}>↑</button>
                        <button className="btn-icon" title="Descendre" disabled={i === fields.length - 1} onClick={() => move(f, 1)}>↓</button>
                        <button className="btn-icon" title="Modifier" onClick={() => { setFieldModal({ ...f }); setErr(''); }}>✏️</button>
                        <button className="btn-icon delete" title="Supprimer" onClick={() => delField(f)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {typeModal && (
        <Modal title={typeModal.id ? 'Modifier le type' : 'Nouveau type de document'} onClose={() => setTypeModal(null)} onSave={saveType}>
          {err && <div className="lf-banner status-error">{err}</div>}
          <Field label="Nom *"><input autoFocus value={typeModal.nom} onChange={(e) => setTypeModal({ ...typeModal, nom: e.target.value })} placeholder="Ex. Liasse fiscale" /></Field>
          <Field label="Description"><textarea rows={2} value={typeModal.description || ''} onChange={(e) => setTypeModal({ ...typeModal, description: e.target.value })} /></Field>
        </Modal>
      )}
      {fieldModal && (
        <Modal title={fieldModal.id ? 'Modifier le champ' : 'Nouveau champ à extraire'} onClose={() => setFieldModal(null)} onSave={saveField}>
          {err && <div className="lf-banner status-error">{err}</div>}
          <Field label="Nom du champ *"><input autoFocus value={fieldModal.nom} onChange={(e) => setFieldModal({ ...fieldModal, nom: e.target.value })} placeholder="Ex. Chiffre d'affaires N" /></Field>
          <Field label="Consigne pour l'IA (où/quoi chercher)"><textarea rows={2} value={fieldModal.description || ''} onChange={(e) => setFieldModal({ ...fieldModal, description: e.target.value })} placeholder="Ex. CA net de l'exercice N, ligne FL du formulaire 2052" /></Field>
          <Field label="Type de donnée"><select value={fieldModal.type_donnee} onChange={(e) => setFieldModal({ ...fieldModal, type_donnee: e.target.value })}>{TYPES_DONNEE.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}</select></Field>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════ Dossiers ════════════════════════════════════════
function DossiersTab({ user, editMode, onOpen }) {
  const [dossiers, setDossiers] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [err, setErr] = useState('');
  const canDelete = editMode && user?.role === 'super-admin';

  const load = useCallback(async () => {
    const [d, t] = await Promise.all([liassesApi.listDossiers(), liassesApi.listTypes()]);
    setDossiers(d.data.dossiers); setTypes(t.data.types); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!modal.nom.trim()) { setErr('Le nom est requis'); return; }
    if (!modal.type_id) { setErr('Le type est requis'); return; }
    try { await liassesApi.createDossier({ nom: modal.nom, type_id: Number(modal.type_id) }); setModal(null); setErr(''); await load(); }
    catch (e) { setErr(e.response?.data?.error || 'Erreur'); }
  };
  const del = async (d) => { if (!window.confirm(`Supprimer le dossier « ${d.nom} » et tous ses documents ?`)) return; await liassesApi.deleteDossier(d.id); await load(); };

  return (
    <>
      <div className="lf-toolbar">
        <button className="btn-primary" onClick={() => { setModal({ nom: '', type_id: types[0]?.id || '' }); setErr(''); }} disabled={types.length === 0}>+ Nouveau dossier</button>
        {types.length === 0 && <span className="lf-muted">Créez d'abord un type dans l'onglet Configuration.</span>}
      </div>
      {loading ? <div className="lf-empty"><div className="lf-spinner" /> Chargement…</div>
        : dossiers.length === 0 ? (
          <div className="lf-empty"><div className="lf-empty-icon">🧾</div><h3>Aucun dossier</h3><p>Créez un dossier (ex. « Concessions groupe 2024 »), importez vos PDF, lancez l'analyse.</p></div>
        ) : (
          <div className="lf-dossier-grid">
            {dossiers.map((d) => (
              <div key={d.id} className="lf-dossier-card" onClick={() => onOpen(d)}>
                <div className="lf-card-head">
                  <h3>{d.nom}</h3>
                  <div className="action-buttons" onClick={(e) => e.stopPropagation()}>{canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => del(d)}>🗑️</button>}</div>
                </div>
                <div className="lf-card-type">{d.type_nom || <span className="lf-muted">type supprimé</span>}</div>
                <div className="lf-card-stats">
                  <span className="lf-chip">📄 {d.nb_documents}</span>
                  {d.nb_indexes > 0 && <span className="lf-chip status-success">✅ {d.nb_indexes} indexé{d.nb_indexes > 1 ? 's' : ''}</span>}
                  {d.nb_extraits > 0 && <span className="lf-chip status-success">📊 {d.nb_extraits} analysé{d.nb_extraits > 1 ? 's' : ''}</span>}
                  {d.nb_en_cours > 0 && <span className="lf-chip status-info">⏳ {d.nb_en_cours}</span>}
                  {d.nb_erreurs > 0 && <span className="lf-chip status-error">⚠️ {d.nb_erreurs}</span>}
                </div>
                <div className="lf-card-foot">{user?.role === 'super-admin' && d.owner_prenom && <span>{d.owner_prenom} {d.owner_nom} · </span>}{relDate(d.updated_at)}</div>
              </div>
            ))}
          </div>
        )}

      {modal && (
        <Modal title="Nouveau dossier d'analyse" onClose={() => setModal(null)} onSave={save}>
          {err && <div className="lf-banner status-error">{err}</div>}
          <Field label="Nom du dossier *"><input autoFocus value={modal.nom} onChange={(e) => setModal({ ...modal, nom: e.target.value })} placeholder="Ex. Concessions groupe 2024" /></Field>
          <Field label="Type de document *"><select value={modal.type_id} onChange={(e) => setModal({ ...modal, type_id: e.target.value })}>{types.map((t) => <option key={t.id} value={t.id}>{t.nom}</option>)}</select></Field>
          <p className="lf-hint">Le type détermine les colonnes (champs à extraire) du tableau de résultats.</p>
        </Modal>
      )}
    </>
  );
}

// ═══════════════════════════ Espace dossier ══════════════════════════════════
function DossierWorkspace({ dossier, user, editMode }) {
  const [sub, setSub] = useState('documents');
  return (
    <div className="page-container lf-workspace">
      <div className="lf-ws-title"><h2>{dossier.nom}</h2><span className="lf-ws-type">{dossier.type_nom}</span></div>
      <div className="page-tabs">
        <button className={sub === 'documents' ? 'active' : ''} onClick={() => setSub('documents')}>📄 Documents</button>
        <button className={sub === 'resultats' ? 'active' : ''} onClick={() => setSub('resultats')}>📊 Résultats</button>
        <button className={sub === 'chat' ? 'active' : ''} onClick={() => setSub('chat')}>💬 Chat</button>
      </div>
      {sub === 'documents' && <DocumentsPane dossier={dossier} canDelete={editMode && user?.role === 'super-admin'} />}
      {sub === 'resultats' && <ResultsPane dossier={dossier} />}
      {sub === 'chat' && <ChatPane dossier={dossier} />}
    </div>
  );
}

// ── Documents (upload masse + statuts) ───────────────────────────────────────
function DocumentsPane({ dossier, canDelete }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rename, setRename] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await liassesApi.listDocuments(dossier.id); setDocuments(r.data.documents); } catch (_) {} finally { setLoading(false); }
  }, [dossier.id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const pending = documents.some((d) => !isTerminal(d.statut));
    if (pending && !pollRef.current) pollRef.current = setInterval(load, 3000);
    else if (!pending && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    return () => { if (pollRef.current && !documents.some((d) => !isTerminal(d.statut))) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [documents, load]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Upload à concurrence limitée (≈5) pour les lots de 50+
  const uploadOne = useCallback(async (file) => {
    setUploads((u) => [...u, { name: file.name, state: 'upload' }]);
    try {
      const { data } = await liassesApi.presign(dossier.id, { filename: file.name, contentType: 'application/pdf' });
      const put = await fetch(data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'application/pdf' }, body: file });
      if (!put.ok) throw new Error('S3');
      await liassesApi.confirm(dossier.id, { key: data.key, filename: file.name, contentType: 'application/pdf', size: file.size });
      setUploads((u) => u.filter((x) => x.name !== file.name));
    } catch (e) {
      try { const form = new FormData(); form.append('file', file); await liassesApi.uploadFile(dossier.id, form); setUploads((u) => u.filter((x) => x.name !== file.name)); }
      catch (e2) { setUploads((u) => u.map((x) => x.name === file.name ? { ...x, state: 'error' } : x)); }
    }
  }, [dossier.id]);

  const handleFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    const rejected = fileList.length - files.length;
    if (rejected > 0) setUploads((u) => [...u, { name: `${rejected} fichier(s) ignoré(s)`, state: 'error' }]);
    const LIMIT = 5; let i = 0;
    const worker = async () => { while (i < files.length) { const f = files[i++]; await uploadOne(f); } };
    await Promise.all(Array.from({ length: Math.min(LIMIT, files.length) }, worker));
    await load();
  }, [uploadOne, load]);

  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); };
  const del = async (d) => { if (!window.confirm(`Supprimer « ${d.libelle || d.nom} » ?`)) return; await liassesApi.deleteDocument(dossier.id, d.id); await load(); };
  const reindex = async (d) => { await liassesApi.reindex(dossier.id, d.id); await load(); };
  const openPreview = async (d) => { try { const { data } = await liassesApi.fileUrl(dossier.id, d.id); setPreview({ url: data.url, nom: d.libelle || d.nom }); } catch (_) { alert('Aperçu indisponible'); } };
  const saveRename = async () => { await liassesApi.renameDocument(dossier.id, rename.id, { libelle: rename.libelle }); setRename(null); await load(); };

  return (
    <div className="lf-docs">
      <input ref={fileRef} type="file" accept="application/pdf" multiple hidden onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
      <div className={`lf-dropzone ${dragOver ? 'over' : ''}`} onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop} onClick={() => fileRef.current?.click()}>
        <div className="lf-dz-icon">⬆️</div>
        <div><strong>Glissez vos PDF ici</strong> ou cliquez — plusieurs dizaines acceptées</div>
        <div className="lf-dz-hint">PDF uniquement · l'indexation démarre automatiquement</div>
      </div>

      {loading ? <div className="lf-empty small"><div className="lf-spinner" /> Chargement…</div>
        : (documents.length === 0 && uploads.length === 0) ? <div className="lf-empty small"><p>Aucun document. Importez vos PDF pour lancer l'indexation.</p></div>
          : (
            <table className="lf-doc-table">
              <thead><tr><th>Société / document</th><th>Taille</th><th>Indexation</th><th>Analyse</th><th></th></tr></thead>
              <tbody>
                {uploads.map((up, i) => (
                  <tr key={`up-${i}`}><td>📄 {up.name}</td><td></td><td colSpan={2}>{up.state === 'error' ? <span className="lf-badge status-error">⚠️ échec upload</span> : <span className="lf-badge status-info"><span className="lf-spinner tiny" /> Envoi…</span>}</td><td></td></tr>
                ))}
                {documents.map((d) => {
                  const si = ING[d.statut] || ING.en_attente; const se = EXT[d.statut_extraction] || EXT.en_attente;
                  return (
                    <tr key={d.id}>
                      <td>
                        <div className="lf-doc-nom" title={d.nom}>📄 {d.libelle || d.nom}</div>
                        {d.libelle && <div className="lf-doc-orig">{d.nom}</div>}
                        {d.statut === 'erreur' && d.erreur && <div className="lf-doc-error">{d.erreur}</div>}
                      </td>
                      <td className="lf-nowrap">{humanSize(d.taille_octets)}{d.statut === 'indexe' && d.nb_pages ? ` · ${d.nb_pages}p` : ''}</td>
                      <td><span className={`lf-badge status-${si.cls}`}>{si.spin ? <span className="lf-spinner tiny" /> : si.icon} {si.label}</span></td>
                      <td><span className={`lf-badge status-${se.cls}`}>{se.spin ? <span className="lf-spinner tiny" /> : se.icon} {se.label}</span></td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-icon" title="Société (nom personnalisé)" onClick={() => setRename({ id: d.id, nom: d.nom, libelle: d.libelle || '' })}>✏️</button>
                          <button className="btn-icon" title="Aperçu" onClick={() => openPreview(d)}>👁️</button>
                          {d.statut === 'erreur' && <button className="btn-icon" title="Réessayer" onClick={() => reindex(d)}>🔄</button>}
                          {canDelete && <button className="btn-icon delete" title="Supprimer" onClick={() => del(d)}>🗑️</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

      {rename && (
        <Modal title="Société (nom personnalisé)" onClose={() => setRename(null)} onSave={saveRename}>
          <Field label="Fichier original"><input value={rename.nom} readOnly className="lf-readonly" /></Field>
          <Field label="Nom affiché (« Société »)"><input autoFocus value={rename.libelle} onChange={(e) => setRename({ ...rename, libelle: e.target.value })} placeholder="Ex. Concession A" /></Field>
        </Modal>
      )}
      {preview && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2>{preview.nom}</h2><button className="modal-close" onClick={() => setPreview(null)}>✕</button></div>
            <div className="modal-body lf-preview-body"><iframe title="Aperçu" src={preview.url} className="lf-preview-frame" /></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Résultats (tableau éditable + analyse + export Excel) ─────────────────────
function ResultsPane({ dossier }) {
  const [fields, setFields] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analysing, setAnalysing] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await liassesApi.resultats(dossier.id); setFields(r.data.fields); setRows(r.data.rows); } catch (_) {} finally { setLoading(false); }
  }, [dossier.id]);
  useEffect(() => { load(); }, [load]);

  // Une extraction est en cours si un document est 'en_cours', ou si on vient de
  // lancer l'analyse et qu'il reste des documents indexés en attente d'extraction.
  const running = rows.some((r) => r.statut_extraction === 'en_cours')
    || (analysing && rows.some((r) => r.statut === 'indexe' && r.statut_extraction === 'en_attente'));
  useEffect(() => {
    if (running && !pollRef.current) pollRef.current = setInterval(load, 3000);
    if (!running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; setAnalysing(false); }
  }, [running, load]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const analyser = async () => {
    setAnalysing(true);
    try { await liassesApi.analyser(dossier.id); await load(); if (!pollRef.current) pollRef.current = setInterval(load, 3000); }
    catch (e) { alert(e.response?.data?.error || 'Erreur'); setAnalysing(false); }
  };

  const editCell = async (docId, fieldId, value) => {
    setRows((rs) => rs.map((r) => r.id === docId ? { ...r, values: { ...r.values, [fieldId]: { valeur: value, edite: true } } } : r));
    try { await liassesApi.updateValue(dossier.id, { document_id: docId, field_id: fieldId, valeur: value }); } catch (_) { load(); }
  };

  const exportExcel = () => {
    const header = ['Société', ...fields.map((f) => f.nom)];
    const data = rows.map((r) => [r.societe, ...fields.map((f) => r.values[f.id]?.valeur ?? '')]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    ws['!cols'] = header.map((h, i) => ({ wch: i === 0 ? 28 : Math.max(14, h.length + 2) }));
    ws['!freeze'] = { xSplit: 1, ySplit: 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Résultats');
    XLSX.writeFile(wb, `liasses_${dossier.nom.replace(/[^a-zA-Z0-9]+/g, '_')}.xlsx`);
  };

  const nbIndexed = rows.filter((r) => r.statut === 'indexe').length;

  if (loading) return <div className="lf-empty small"><div className="lf-spinner" /> Chargement…</div>;
  if (fields.length === 0) return <div className="lf-empty"><p>Aucun champ configuré pour le type de ce dossier. Ajoutez des champs dans l'onglet Configuration.</p></div>;

  return (
    <div className="lf-results">
      <div className="lf-results-bar">
        <button className="btn-primary" onClick={analyser} disabled={running || nbIndexed === 0}>{running ? '⚙️ Analyse en cours…' : '▶️ Lancer l\'analyse'}</button>
        <button className="btn-secondary" onClick={exportExcel} disabled={rows.length === 0}>⬇️ Export Excel</button>
        {nbIndexed === 0 && <span className="lf-muted">Importez et indexez des PDF (onglet Documents) avant d'analyser.</span>}
        <span className="lf-muted lf-right">L'IA peut se tromper — cliquez une cellule pour corriger.</span>
      </div>
      {rows.length === 0 ? <div className="lf-empty small"><p>Aucun document. Importez des PDF dans l'onglet Documents.</p></div> : (
        <div className="lf-table-scroll">
          <table className="lf-result-table">
            <thead><tr><th className="lf-sticky-col">Société</th>{fields.map((f) => <th key={f.id}>{f.nom}</th>)}<th>Statut</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const se = EXT[r.statut_extraction] || EXT.en_attente;
                return (
                  <tr key={r.id}>
                    <td className="lf-sticky-col lf-societe" title={r.nom}>{r.societe}</td>
                    {fields.map((f) => {
                      const cell = r.values[f.id];
                      return (
                        <td key={f.id} className={cell?.edite ? 'lf-cell-edited' : ''}>
                          <input className="lf-cell-input" defaultValue={cell?.valeur ?? ''} placeholder="—"
                            onBlur={(e) => { const v = e.target.value; if (v !== (cell?.valeur ?? '')) editCell(r.id, f.id, v); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} />
                        </td>
                      );
                    })}
                    <td><span className={`lf-badge status-${se.cls}`}>{se.spin ? <span className="lf-spinner tiny" /> : se.icon} {se.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Chat ─────────────────────────────────────────────────────────────────────
function ChatPane({ dossier }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    liassesApi.conversations(dossier.id).then((r) => {
      const h = [];
      for (const c of r.data.conversations || []) { h.push({ role: 'user', content: c.question }); h.push({ role: 'assistant', content: c.answer, sources: c.sources || [], empty: !c.answer }); }
      setMessages(h);
    }).catch(() => {});
  }, [dossier.id]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }); }, [messages, sending]);

  const send = async () => {
    const q = input.trim(); if (!q || sending) return;
    setInput(''); setMessages((m) => [...m, { role: 'user', content: q }]); setSending(true);
    try { const { data } = await liassesApi.chat(dossier.id, q); setMessages((m) => [...m, { role: 'assistant', content: data.answer, sources: data.sources || [], empty: !data.answer, message: data.message }]); }
    catch (e) { setMessages((m) => [...m, { role: 'assistant', content: null, error: e.response?.data?.error || 'Erreur' }]); }
    finally { setSending(false); }
  };

  const suggestions = ["Donne-moi l'évolution du chiffre d'affaires", 'Quels sont les plus gros sujets que tu identifies ?', 'Fais-moi un rapport d\'audit synthétique'];

  return (
    <div className="lf-chat">
      <div className="lf-chat-messages" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="lf-empty small">
            <div className="lf-empty-icon">💬</div>
            <p>Interrogez les documents du dossier : analyses, tendances, comparaisons entre sociétés, audit…</p>
            <div className="lf-suggests">{suggestions.map((s, i) => <button key={i} className="lf-suggest" onClick={() => setInput(s)}>{s}</button>)}</div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`lf-msg ${m.role}`}>
            <div className="lf-msg-bubble">
              {m.error ? <span className="lf-msg-error">⚠️ {m.error}</span>
                : m.empty ? <em>{m.message || "Je n'ai pas trouvé d'élément pertinent."}</em>
                  : <span className="lf-msg-text">{m.content}</span>}
              {m.sources && m.sources.length > 0 && (
                <div className="lf-sources">
                  <div className="lf-sources-title">Sources</div>
                  {m.sources.map((s, j) => (
                    <div key={j} className="lf-source"><span className="lf-source-doc">📄 {s.nom} · p.{s.page}</span><div className="lf-source-extract">« {s.extrait} »</div></div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && <div className="lf-msg assistant"><div className="lf-msg-bubble"><span className="lf-typing"><i /><i /><i /></span></div></div>}
      </div>
      <div className="lf-chat-input">
        <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Posez votre question… (Entrée pour envoyer)" rows={2} disabled={sending} />
        <button className="btn-primary" onClick={send} disabled={sending || !input.trim()}>{sending ? '…' : 'Envoyer'}</button>
      </div>
    </div>
  );
}

// ── Petits composants partagés ───────────────────────────────────────────────
function Modal({ title, children, onClose, onSave }) {
  return (
    <div className="modal-overlay">
      <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header"><h2>{title}</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">{children}</div>
        <div className="modal-footer"><button className="btn-secondary" onClick={onClose}>Annuler</button><button className="btn-primary" onClick={onSave}>Enregistrer</button></div>
      </div>
    </div>
  );
}
function Field({ label, children }) { return <label className="lf-field"><span>{label}</span>{children}</label>; }
